import { apiClient } from '../utils/api-client';
import type { User } from '../types/user';
import type { CreatedClimbsResponse } from '../types/send';

export interface UserProfileResponse {
  data: {
    id: string;
    type: 'user';
    attributes: User;
  };
}

/** The climber's QR code: identity, the signed code, and the URL it encodes. */
export interface UserQrCode {
  username: string;
  full_name: string;
  profile_url: string;
  avatar_url: string | null;
  /** Profile URL with the signed code attached — what the QR image encodes. */
  qr_url: string;
  code: string;
  expires_at: string;
}

export interface UserQrCodeResponse {
  data: {
    id: string;
    type: 'user_qr_code';
    attributes: UserQrCode;
  };
}

/** Who a scanned code belongs to, once the backend has vouched for it. */
export interface UserQrCodeVerification {
  valid: true;
  username: string;
  full_name: string;
  slug: string;
}

export interface UserQrCodeVerificationResponse {
  data: {
    id: string;
    type: 'user_qr_code_verification';
    attributes: UserQrCodeVerification;
  };
}

class UsersService {
  async getUserBySlug(slug: string): Promise<User> {
    const response = await apiClient.get(`/api/users/${slug}`, {});

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found');
      }
      throw new Error('Failed to fetch user profile');
    }

    const data: UserProfileResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * The climbs a person put up, newest first, ten at a time — the deleted ones
   * included, marked `discarded`; `meta.has_more` says whether to ask for the
   * next page.
   */
  async getCreatedClimbs(userSlug: string, page: number): Promise<CreatedClimbsResponse> {
    const response = await apiClient.get(`/api/users/${userSlug}/climbs?page=${page}`, {});

    if (!response.ok) {
      throw new Error('Failed to fetch the climbs created by the user');
    }

    return await response.json();
  }

  /**
   * Mints a fresh QR payload for the signed-in climber. The backend only issues
   * one for the caller's own profile, and each call returns a new code.
   */
  async getQrCode(userSlug: string): Promise<UserQrCode> {
    const response = await apiClient.get(`/api/users/${userSlug}/qr_code`, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to generate QR code');
    }

    const data: UserQrCodeResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Checks a code scanned from a profile QR. Returns null when the code is not
   * valid for this profile or has expired — an expected outcome, not a failure,
   * so the automatic error toast is suppressed and the caller decides.
   */
  async verifyQrCode(userSlug: string, code: string): Promise<UserQrCodeVerification | null> {
    const response = await apiClient.post(
      `/api/users/${userSlug}/qr_code/verify`,
      { code },
      { requireAuth: true, showErrors: false }
    );

    if (!response.ok) {
      return null;
    }

    const data: UserQrCodeVerificationResponse = await response.json();
    return data.data.attributes;
  }

  async followUser(userSlug: string): Promise<void> {
    const response = await apiClient.post(`/api/users/${userSlug}/follow`, {}, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to follow user');
    }
  }

  async unfollowUser(userSlug: string): Promise<void> {
    const response = await apiClient.delete(`/api/users/${userSlug}/unfollow`, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to unfollow user');
    }
  }

  async inviteUser(params: {
    email: string;
    inviteeName: string;
    message?: string;
    captchaToken: string;
  }): Promise<{ message: string; resent?: boolean }> {
    const response = await apiClient.post(
      '/api/users/invitation',
      {
        email: params.email,
        invitee_name: params.inviteeName,
        message: params.message,
        captcha_token: params.captchaToken,
      },
      { requireAuth: true }
    );

    if (!response.ok) {
      const errorData = await response.json() as { error?: string; message?: string };
      throw new Error(errorData.error || errorData.message || 'Failed to send invitation');
    }

    return await response.json();
  }

  async validateInvitationToken(token: string): Promise<{
    user: { id: number; email: string; name: string | null; surname: string | null; invitation_token: string };
    /** Set when a gym registered this person at the desk. */
    gym_membership?: { gym_name: string | null; member_name: string } | null;
    /** Set when a competition's desk entered this person. */
    competition_entry?: { event_name: string; entrant_name: string } | null;
    /** Set when a place invited this address onto its staff — a competition's judge. */
    staff_invitation?: { place_name: string; staff_roles: string[] } | null;
  }> {
    const response = await apiClient.get(`/api/users/invitation/validate?invitation_token=${token}`, {});

    if (!response.ok) {
      const errorData = await response.json() as { error?: string };
      throw new Error(errorData.error || 'Invalid invitation token');
    }

    return await response.json();
  }

  /**
   * Attaches the gym membership behind an invitation to the account already
   * signed in, instead of registering as the invited address.
   */
  async claimInvitation(token: string): Promise<{ message: string }> {
    const response = await apiClient.post(
      '/api/users/invitation/claim',
      { invitation_token: token },
      { requireAuth: true, showErrors: false }
    );

    if (!response.ok) {
      const errorData = await response.json() as { error?: string };
      throw new Error(errorData.error || 'Could not link the membership');
    }

    return await response.json();
  }

  async acceptInvitation(data: {
    invitation_token: string;
    name: string;
    surname: string;
    username: string;
    password: string;
    password_confirmation: string;
  }): Promise<{
    token: string;
    refresh_token: string;
    resource_owner: { user?: User } | User;
    message: string;
  }> {
    const response = await apiClient.put('/api/users/invitation', data, {});

    if (!response.ok) {
      const errorData = await response.json() as { error?: string; message?: string };
      throw new Error(errorData.error || errorData.message || 'Failed to accept invitation');
    }

    return await response.json();
  }
}

export const usersService = new UsersService();
